/**
 * Permission catalog — the SINGLE SOURCE OF TRUTH for every sensitive backend operation
 * in the Brokly platform (Epic 12 Dynamic RBAC, Story 12.1).
 *
 * DESIGN DECISIONS baked in:
 *
 * 1. CATALOG = CODE, NOT A TABLE. The DB stores only the *selected* keys per role
 *    (role_permission rows built in 12.2). The catalog itself is never persisted;
 *    keys can be added in future epics with no migration.
 *
 * 2. `admin` IS THE WILDCARD ROLE — NOT A KEY. See the comment block below at
 *    PERMISSION_CATALOG. Do not add a wildcard / `*` / `admin.*` key here.
 *
 * 3. Every key is dotted lowercase-snake-case, globally unique. Most keys are the
 *    classic `category.operation`; the Berechtigungskonzept V3.0 (WS0) keys use a
 *    deeper `category.sub.operation` namespace (e.g.
 *    `admin.team.eigene_organisation.verwalten`). The format invariant accepts one or
 *    more dotted lowercase-snake segments.
 *    "No magic values" — import these constants, never inline the strings.
 *    [Source: docs/architecture/coding-standards.md line 28]
 *
 * 4. `as const satisfies readonly PermissionDefinition[]` keeps each `key` as a
 *    narrow string-literal type (for the PermissionKey union) while TypeScript still
 *    checks the shape against PermissionDefinition (AC6).
 *    [Source: docs/architecture/tech-stack.md — TypeScript ^5.8.x / `satisfies`]
 *
 * [Source: docs/prd/epic-12-dynamic-rbac.md "Permission catalog" + "Seeding (decision #2)"]
 * [Source: docs/architecture/source-tree.md line 85 — shared types defined once here]
 */

import type { MembershipRole } from './auth';

// ── Types ─────────────────────────────────────────────────────────────────────

/**
 * The four operation verbs used across the catalog.
 * 'read'    — view / list data
 * 'write'   — create / update / delete data
 * 'approve' — confirm/reject a consequential state change (override, merge, grant)
 * 'execute' — trigger a machine process or batch pipeline
 */
export type PermissionOp = 'read' | 'write' | 'approve' | 'execute';

/**
 * Shape of a single permission definition in the catalog.
 *
 * `key`         — `category.operation`, lowercase-snake, globally unique.
 * `category`    — must be one of the PERMISSION_CATEGORIES keys.
 * `op`          — one of the four PermissionOp verbs.
 * `label`       — short wizard checkbox label (one line, user-visible).
 * `description` — wizard helper text (one line; explains what the permission enables).
 */
export interface PermissionDefinition {
  key: string;
  category: string;
  op: PermissionOp;
  label: string;
  description: string;
}

/**
 * Shape of a single wizard section (category).
 *
 * `key`   — lowercase-snake identifier; used by permission entries' `category` field.
 * `label` — display name for the wizard section header.
 */
export interface PermissionCategory {
  key: string;
  label: string;
}

// ── Wizard categories (ordered) ───────────────────────────────────────────────

/**
 * Ordered list of wizard section headers.
 * Order is the display order in the role-creation wizard (decision 5).
 * Every catalog entry's `category` MUST match one of these keys.
 *
 * [Source: docs/prd/epic-12-dynamic-rbac.md "permission_categories (wizard sections, in display order)"]
 * [Source: Story 12.1 AC3 — exact 17-entry display order]
 */
export const PERMISSION_CATEGORIES = [
  { key: 'crm',            label: 'People & companies' },
  { key: 'merge',          label: 'Dedup & merge' },
  { key: 'tags',           label: 'Tags' },
  { key: 'customer_status',label: 'Customer status' },
  { key: 'mandate',        label: 'Mandate & Mahnwesen' },
  { key: 'blacklist',      label: 'Blacklist' },
  { key: 'suppression',    label: 'Ausschlussliste' },
  { key: 'intake',         label: 'Intake & webhooks' },
  { key: 'doc_pipeline',   label: 'Documents & excerpts' },
  { key: 'routing',        label: 'Routing & confidence' },
  { key: 'comms',          label: 'Communication & access' },
  { key: 'admin',          label: 'Tenant admin' },
  { key: 'access',         label: 'Roles & permissions' },
  { key: 'audit',          label: 'Audit log' },
  { key: 'company_master', label: 'Company register (global)' },
  { key: 'policy',         label: 'Policies' },
  { key: 'invoice_check',  label: 'Rechnungskontrolle' },
  { key: 'commission',     label: 'Courtageabrechnung' },
  { key: 'renewals',         label: 'Renewals' },
  { key: 'customer_portal',  label: 'Customer portal' },
  { key: 'agent',            label: 'Brok assistant' },
  { key: 'doc_generation',   label: 'Document generation' },
  { key: 'channels',         label: 'Kanalsteuerung' },
  { key: 'signing',          label: 'Zeichnungsprozess' },
  { key: 'funktionsgruppen', label: 'Funktionsgruppen (task teams)' },
  { key: 'search',           label: 'Global search' },
  { key: 'placement',        label: 'Ausschreibungen (Placements)' },
] as const satisfies readonly PermissionCategory[];

/** Derived union type of every valid category key. */
export type PermissionCategoryKey = (typeof PERMISSION_CATEGORIES)[number]['key'];

// ── Permission catalog ────────────────────────────────────────────────────────

/**
 * !! DO NOT add a wildcard / `*` / `admin.*` / `all` key to this catalog. !!
 *
 * The `admin` role is an IMMUTABLE WILDCARD ROLE resolved in the guard (Story 12.4).
 * It is NOT a catalog key. The DB stores zero `role_permission` rows for `admin`;
 * that is what makes new keys live for admins with no migration.
 *
 * [Source: docs/prd/epic-12-dynamic-rbac.md decisions 2 + 4]
 * [Source: Story 12.1 AC5]
 */
export const PERMISSION_CATALOG = [

  // ── crm ──────────────────────────────────────────────────────────────────
  // [Source: firms.controller.ts:43 GET /firms/:id; person/firma detail+lists]
  {
    key: 'crm.read',
    category: 'crm',
    op: 'read',
    label: 'View people & companies',
    description: 'Read person and firma details, lists, and search results.',
  },
  // [Source: person/firma create+update — intake genesis + manual edits]
  {
    key: 'crm.write',
    category: 'crm',
    op: 'write',
    label: 'Edit people & companies',
    description: 'Create and update person and firma records.',
  },
  // [Source: relationships.controller.ts:215,238 GET /persons/:id/relationships, GET /firms/:id/relationships]
  {
    key: 'relationship.read',
    category: 'crm',
    op: 'read',
    label: 'View relationships',
    description: 'Read person-firma, person-person, and firma-firma relationships.',
  },
  // [Source: relationships.controller.ts:65–186 person-firma/person-person/firma-firma @Post/@Patch/@Delete]
  {
    key: 'relationship.write',
    category: 'crm',
    op: 'write',
    label: 'Edit relationships',
    description: 'Create, update, and delete relationships between persons and firmas.',
  },
  // [Source: firms.controller.ts:63 POST /firms/:id/enrich]
  {
    key: 'firma.enrich',
    category: 'crm',
    op: 'execute',
    label: 'Trigger register enrichment',
    description: 'Manually trigger a Zefix/UID re-fetch for a firma.',
  },
  // P2 Placement Slice 1 — the Kunde-Ebene «Risikoprofil» tab (generelles Risikoprofil +
  // Basissparten-Checkliste). read/write are in BASIC_BASELINE_KEYS (every broker captures a
  // customer's risk profile — "ab Broker Advisor" = default visible/editable). The besonders
  // schuetzenswerten Felder (Gesundheit/Einkommen) have their OWN field-right + consent gate on
  // top (field-permission.ts) — this key alone never reveals them.
  // [Source: customer-risk.controller.ts GET/PATCH /customers/:t/:id/risk-profile + coverage-baseline]
  {
    key: 'customer_risk.read',
    category: 'crm',
    op: 'read',
    label: 'View customer risk profile',
    description: 'Read a customer\'s generelles Risikoprofil and Basissparten-Checkliste (Kunde-Ebene).',
  },
  {
    key: 'customer_risk.write',
    category: 'crm',
    op: 'write',
    label: 'Edit customer risk profile',
    description: 'Create and update a customer\'s Risikoprofil and Basissparten assessment (Kunde-Ebene).',
  },

  // ── merge ─────────────────────────────────────────────────────────────────
  // [Source: merge.controller.ts:80,93 GET /persons/:id/duplicates, /firms/:id/duplicates]
  {
    key: 'person.merge_suggestions',
    category: 'merge',
    op: 'read',
    label: 'View duplicate suggestions',
    description: 'List auto-detected duplicate person/firma candidates.',
  },
  // [Source: merge.controller.ts:64-65 POST /merge @Roles(ROLE_ADMIN) — destructive/irreversible]
  {
    key: 'person.merge',
    category: 'merge',
    op: 'approve',
    label: 'Execute a merge',
    description: 'Irreversibly merge two person or firma records (admin-only).',
  },

  // ── tags ──────────────────────────────────────────────────────────────────
  // [Source: tags.controller.ts:145 GET /tags; :122 POST /tags/filter]
  {
    key: 'tag.read',
    category: 'tags',
    op: 'read',
    label: 'View tags',
    description: 'List tag definitions and filter records by tag.',
  },
  // [Source: tags.controller.ts:79 POST /tags/assign; :105 POST /tags/unassign]
  {
    key: 'tag.assign',
    category: 'tags',
    op: 'write',
    label: 'Assign / unassign tags',
    description: 'Attach or remove existing tags on records.',
  },
  // [Source: tags.controller.ts:135 POST /tags @Roles(ROLE_ADMIN); :156 PATCH /tags/:id @Roles(ROLE_ADMIN); :173 DELETE /tags/:id @Roles(ROLE_ADMIN)]
  {
    key: 'tag_definition.manage',
    category: 'tags',
    op: 'write',
    label: 'Manage tag definitions',
    description: 'Create, rename, and delete tag definitions (admin-only).',
  },

  // ── customer_status ───────────────────────────────────────────────────────
  // [Source: customer-status.controller.ts:72 GET /customer-status/:t/:id; :90 .../history]
  {
    key: 'customer_status.read',
    category: 'customer_status',
    op: 'read',
    label: 'View customer status',
    description: 'Read the current status and history of a customer.',
  },
  // [Source: normal legal status transitions (state-machine)]
  {
    key: 'customer_status.transition',
    category: 'customer_status',
    op: 'write',
    label: 'Transition customer status',
    description: 'Move a customer through allowed state-machine transitions.',
  },
  // [Source: customer-status.controller.ts:110-111 PUT /customer-status/:t/:id @Roles(ROLE_ADMIN) — bypasses LEGAL_TRANSITIONS]
  {
    key: 'customer_status.override',
    category: 'customer_status',
    op: 'approve',
    label: 'Override customer status',
    description: 'Force-set customer status bypassing the legal transition table (admin-only).',
  },

  // ── mandate ───────────────────────────────────────────────────────────────
  // [Source: mandate.controller.ts POST /mandates/prepare, /render, /release, /cancel]
  {
    key: 'mandate.dispatch',
    category: 'mandate',
    op: 'execute',
    label: 'Dispatch mandates',
    description: 'Trigger mandate dispatch for a customer.',
  },
  // [Source: eingangskontrolle.controller.ts:56 POST /eingangskontrolle/return; mandate-termination.controller.ts POST /mandates/terminations]
  {
    key: 'mandate.eingangskontrolle',
    category: 'mandate',
    op: 'write',
    label: 'Eingangskontrolle & Kündigung',
    description: 'Process incoming mandate control and cancellation operations.',
  },
  // [Source: versandboard.controller.ts:56 GET /vorgaenge/board]
  {
    key: 'vorgang.read',
    category: 'mandate',
    op: 'read',
    label: 'View Versandboard',
    description: 'Read the Mahnwesen / Versandboard overview.',
  },
  // [Source: versandboard.controller.ts:88,106,125 POST /vorgaenge/:id/mahnen|abbrechen|eskalieren]
  {
    key: 'vorgang.work',
    category: 'mandate',
    op: 'write',
    label: 'Work Vorgänge',
    description: 'Execute mahnen, abbrechen, and eskalieren actions on Vorgänge.',
  },
  // [Source: mahnwesen-config.controller.ts:50 PUT /mahnwesen-config — dunning-stage config; admin-only seed]
  {
    key: 'mahnwesen_config.manage',
    category: 'mandate',
    op: 'write',
    label: 'Manage Mahnwesen config',
    description: 'Configure dunning stages and thresholds (admin-seeded, not in basic).',
  },

  // ── blacklist ─────────────────────────────────────────────────────────────
  // [Source: blacklist.controller.ts:46 @RequireCapability('blacklist') — GET/POST/PATCH/DELETE /admin/blacklist]
  {
    key: 'blacklist.access',
    category: 'blacklist',
    op: 'read',
    label: 'Access blacklist',
    description: 'View and manage the tenant blacklist (capability-gated; granted separately).',
  },

  // ── suppression (Ausschlussliste) ───────────────────────────────────────────
  // [Source: suppression.controller.ts GET/POST/PATCH/DELETE /admin/suppression — Fix 6]
  // Broker CRUD over the customer-suppression list (block VN genesis for excluded
  // customers). Admin-gated (NOT in basic baseline) — mirrors whitelist.manage.
  {
    key: 'suppression.manage',
    category: 'suppression',
    op: 'write',
    label: 'Manage Ausschlussliste',
    description: 'View and edit the customer-suppression list (customers whose mail never creates a VN).',
  },

  // ── intake ────────────────────────────────────────────────────────────────
  // [Source: intake.controller.ts:62 POST /intake/email — machine-facing system endpoint]
  {
    key: 'intake.ingest',
    category: 'intake',
    op: 'execute',
    label: 'Ingest inbound email',
    description: 'POST to the email intake endpoint (machine/system use; not in basic).',
  },
  // [Source: webhooks.controller.ts:69 POST /webhooks/register-mutation — machine-facing]
  {
    key: 'intake.webhook',
    category: 'intake',
    op: 'execute',
    label: 'Register mutation webhook',
    description: 'Trigger a register-mutation webhook (machine/system use; not in basic).',
  },

  // ── doc_pipeline ──────────────────────────────────────────────────────────
  // [Source: excerpt-upload.controller.ts:62 POST /firms/:id/excerpt]
  {
    key: 'document.upload',
    category: 'doc_pipeline',
    op: 'write',
    label: 'Upload documents',
    description: 'Upload cantonal excerpts and other documents for a firma.',
  },
  // [Source: excerpt-upload.controller.ts:104 GET /firms/:id/cantonal-excerpt-link]
  {
    key: 'document.read_link',
    category: 'doc_pipeline',
    op: 'read',
    label: 'View document links',
    description: 'Retrieve the download link for a cantonal excerpt.',
  },
  // DMS M3 (dms-permissions) — Ablage grid + inline PDF viewer reads. In
  // BASIC_BASELINE_KEYS (every broker browses/opens documents).
  // [Source: dms/spec.md §6 — GET /documents, GET /documents/:id, GET /documents/:id/content]
  {
    key: 'document.read',
    category: 'doc_pipeline',
    op: 'read',
    label: 'View documents',
    description: 'List and view documents, their detail, and the inline PDF viewer.',
  },
  // DMS M3 (dms-permissions) — single + batch document retrieval. In
  // BASIC_BASELINE_KEYS (every broker downloads their own documents).
  // [Source: dms/spec.md §6 — GET /documents/:id/download, POST /documents/batch/download]
  {
    key: 'document.download',
    category: 'doc_pipeline',
    op: 'read',
    label: 'Download documents',
    description: 'Download a single document or a batch of documents as a ZIP.',
  },
  // DMS M3 (dms-permissions) — toggle active/inactive (single + batch). In
  // BASIC_BASELINE_KEYS (every broker manages document state).
  // [Source: dms/spec.md §6 — PATCH /documents/:id/state, POST /documents/batch/state]
  {
    key: 'document.set_state',
    category: 'doc_pipeline',
    op: 'write',
    label: 'Set document state',
    description: 'Toggle a document (or a batch of documents) between active and inactive.',
  },
  // DMS docs-fix (manual reclassify) — edit an existing document's metadata
  // (kind + linked object) via PATCH /documents/:id. In BASIC_BASELINE_KEYS
  // (a broker reclassifying/re-assigning a document is an ordinary Ablage
  // operation — mirrors document.set_state, a write on the same document row).
  // [Source: dms/spec.md §6 — PATCH /documents/:id]
  {
    key: 'document.update',
    category: 'doc_pipeline',
    op: 'write',
    label: 'Edit document metadata',
    description: 'Change a document’s kind and its linked object (assign it to a Police or Kunde).',
  },
  // DMS M3 (dms-permissions) — hard delete (single + batch). Admin-gated —
  // NOT in BASIC_BASELINE_KEYS (mirrors extraction_feedback.submit / policy.migrate).
  // [Source: dms/spec.md §6 — DELETE /documents/:id, POST /documents/batch/delete]
  {
    key: 'document.delete',
    category: 'doc_pipeline',
    op: 'write',
    label: 'Delete documents',
    description: 'Permanently erase an inactive document, its links, and its stored blob (admin-only).',
  },
  // Story V10-I — Docpier extraction-correction feedback (api-side seam 16/18 flip).
  // Admin-gated (NOT in BASIC_BASELINE_KEYS) — mirrors routing_policy.manage.
  // [Source: extraction-feedback.controller.ts POST /admin/extraction-feedback]
  {
    key: 'extraction_feedback.submit',
    category: 'doc_pipeline',
    op: 'write',
    label: 'Submit extraction feedback',
    description:
      'Submit corrected document-extraction results back to the Docpier IDP provider (admin-only).',
  },

  // ── routing ───────────────────────────────────────────────────────────────
  // [Source: admin.controller.ts:93 GET /admin/confidence-policy — class @Roles('admin')]
  {
    key: 'routing_policy.read',
    category: 'routing',
    op: 'read',
    label: 'View routing policy',
    description: 'Read the confidence routing policy configuration (admin-only).',
  },
  // [Source: admin.controller.ts:103 PUT /admin/confidence-policy/:process]
  {
    key: 'routing_policy.manage',
    category: 'routing',
    op: 'write',
    label: 'Manage routing policy',
    description: 'Update the confidence routing policy for each process (admin-only).',
  },

  // ── comms ─────────────────────────────────────────────────────────────────
  // [Source: communication.controller.ts:78 GET /communications (tier-projected)]
  {
    key: 'communication.read',
    category: 'comms',
    op: 'read',
    label: 'View communications',
    description: 'List and read communications (tier-projected based on access grants).',
  },
  // [Source: communication.controller.ts:150 POST /communications/:commId/access-requests]
  {
    key: 'comm_access.request',
    category: 'comms',
    op: 'write',
    label: 'Request communication access',
    description: 'Submit an access request for a restricted communication.',
  },
  // [Source: communication.controller.ts:175 POST /communications/access-requests/:id/decision]
  {
    key: 'comm_access.decide',
    category: 'comms',
    op: 'approve',
    label: 'Decide on access requests',
    description: 'Approve or reject a communication access request.',
  },
  // [Source: communication.controller.ts:203 POST /communications/access-grants — D73-MANAGED-ADMIN-ONLY]
  {
    key: 'comm_access.grant_managed',
    category: 'comms',
    op: 'approve',
    label: 'Grant standing access',
    description: 'Create a standing Managed-Access grant for a communication (admin-only).',
  },
  // Story UI-U8: manual email referencing (Kap. 7) — POST/DELETE /communications/:commId/references.
  // In BASIC_BASELINE_KEYS (every broker hand-files the emails auto-matching missed), with the
  // established no-top-up caveat: already-seeded tenants' non-admin roles need a 12.6 grant.
  // [Source: comm-reference.controller.ts POST/DELETE /communications/:commId/references]
  {
    key: 'comm_reference.manage',
    category: 'comms',
    op: 'write',
    label: 'Reference emails to records',
    description: 'Manually link (and unlink) a communication to a Kunde, Police, or Schaden.',
  },
  // Story MC5: outbound send from a connected mailbox (Kap. 7) — POST /mailbox/send.
  // In BASIC_BASELINE_KEYS (any broker sends from their OWN delegated-OAuth mailbox —
  // NOT an admin gate like the mailbox-CONFIG routes), with the established no-top-up
  // caveat: already-seeded tenants' non-admin roles need a 12.6 grant.
  // [Source: mailbox-send.controller.ts POST /mailbox/send]
  {
    key: 'communication.send',
    category: 'comms',
    op: 'write',
    label: 'Send email from a connected mailbox',
    description: 'Send a reply or new email as yourself from your connected mailbox.',
  },

  // ── admin ─────────────────────────────────────────────────────────────────
  // [Source: admin.controller.ts:60 GET /admin/whitelist — class @Roles('admin')]
  {
    key: 'whitelist.read',
    category: 'admin',
    op: 'read',
    label: 'View whitelist',
    description: 'Read the tenant email/domain whitelist (admin-only).',
  },
  // [Source: admin.controller.ts:66 POST /admin/whitelist; :77 PATCH /admin/whitelist/:id]
  {
    key: 'whitelist.manage',
    category: 'admin',
    op: 'write',
    label: 'Manage whitelist',
    description: 'Add and update tenant email/domain whitelist entries (admin-only).',
  },
  // [Source: admin.controller.ts:116 GET /admin/tenant]
  {
    key: 'tenant.read',
    category: 'admin',
    op: 'read',
    label: 'View tenant settings',
    description: 'Read tenant configuration and metadata (admin-only).',
  },
  // [Source: admin.controller.ts:122 PATCH /admin/tenant]
  {
    key: 'tenant.manage',
    category: 'admin',
    op: 'write',
    label: 'Manage tenant settings',
    description: 'Update tenant configuration and metadata (admin-only).',
  },
  // Berechtigungskonzept V3.0 (WS0) — the two keys the later feature waves build on.
  // Deeper-namespaced (category.sub.operation) than the classic category.operation keys;
  // still lowercase-snake dotted (the format invariant now accepts ≥1 dotted segment).
  // [Source: meetings/2026-08-24_ANALYSIS_berechtigungskonzept.md §2.8 + UC1]
  {
    key: 'admin.team.eigene_organisation.verwalten',
    category: 'admin',
    op: 'write',
    label: 'Manage own-organisation team',
    description:
      'Manage membership of the non-deletable "Eigene Organisation" system team (grants no customer access on its own).',
  },
  // Berechtigungskonzept V3.0 · Bereich 2 (WS2) — restricted-team administration.
  // Separate from admin.team.eigene_organisation.verwalten (which gates ONLY the system-team
  // membership list): these gate the restricted-team admin surface (create/rename/delete a
  // team, manage its members, assign customers to it). read = view teams + members + scopes;
  // manage = mutate them (incl. the scope-bypass customer picker over all customers).
  {
    key: 'admin.team.read',
    category: 'admin',
    op: 'read',
    label: 'View teams',
    description:
      'List customer-visibility teams, their members, and their assigned customers (Bereich 2).',
  },
  {
    key: 'admin.team.manage',
    category: 'admin',
    op: 'write',
    label: 'Manage teams',
    description:
      'Create, rename, and delete restricted-visibility teams; manage their members and assign customers (Bereich 2).',
  },
  // [Source: meetings/2026-08-24_ANALYSIS_berechtigungskonzept.md §2.5 (Bereich 5) + UC9]
  {
    key: 'administration.kontrolle.rueckstand.einsehen',
    category: 'admin',
    op: 'read',
    label: 'View control backlog',
    description:
      'Read-only Kontrollfunktion: view the cross-customer control backlog (Rückstand); never reveals private-marked content.',
  },
  // Bereich 5 Kontrollbereich Cockpit (Berechtigungskonzept V4.0, Kapitel 6) — the Zuweisungs-Recht.
  // REQUIRES administration.kontrolle.rueckstand.einsehen (a role that can assign must first see the
  // list; documented dependency — the role wizard co-selects einsehen). NOT in BASIC_BASELINE_KEYS and
  // NOT auto-granted to the admin group (admins keep it via the wildcard). Every Zuweisung is audited
  // with elevated severity (the list deliberately exceeds the caller's Bereich-2 customer scope).
  // [Source: V4.0-inbox/Kontrollbereich_Cockpit_Entwurf_V0.1.docx.txt Kapitel 6]
  {
    key: 'administration.kontrolle.rueckstand.zuweisen',
    category: 'admin',
    op: 'write',
    label: 'Assign control backlog items',
    description:
      'Assign a control-backlog element (Rückstand) to a person or Aufgaben-Funktionsgruppe from the detail view. Requires administration.kontrolle.rueckstand.einsehen.',
  },

  // ── access ────────────────────────────────────────────────────────────────
  // NOTE: `access` is the role-admin surface (the wizard itself), distinct from the
  // admin wildcard. `role.read`/`role.manage` are gated by the `role.manage` guard
  // in Story 12.6.
  // [Source: Story 12.1 AC2; docs/prd/epic-12-dynamic-rbac.md "Role-management API"]
  {
    key: 'role.read',
    category: 'access',
    op: 'read',
    label: 'View roles & permissions',
    description: 'List roles, their permission sets, and the permission catalog (wizard render).',
  },
  {
    key: 'role.manage',
    category: 'access',
    op: 'write',
    label: 'Manage roles & permissions',
    description: 'Create, rename, delete roles; set permissions; assign/unassign roles to users.',
  },
  // Story V10-E: the data-visibility axis ("Informationen") — deny-list config.
  // visibility.read_own is in BASIC_BASELINE_KEYS (every user fetches their own
  // effective denied set for UI shaping); read/manage are the admin config surface.
  // [Source: effective-visibility.controller.ts GET /me/effective-visibility]
  {
    key: 'visibility.read_own',
    category: 'access',
    op: 'read',
    label: 'View own visibility',
    description: "Fetch the caller's effective denied visibility keys (UI shaping).",
  },
  // [Source: visibility-deny.controller.ts GET /admin/visibility/catalog + GET /admin/roles/:roleId/visibility-denies]
  {
    key: 'visibility.read',
    category: 'access',
    op: 'read',
    label: 'View visibility config',
    description: 'Read the visibility catalog and per-role deny sets (admin surface).',
  },
  // [Source: visibility-deny.controller.ts PUT /admin/roles/:roleId/visibility-denies]
  {
    key: 'visibility.manage',
    category: 'access',
    op: 'write',
    label: 'Manage visibility denies',
    description: "Replace a role's denied visibility keys (deny-list config).",
  },

  // ── audit ─────────────────────────────────────────────────────────────────
  // [Source: audit.controller.ts:86 GET /audit @Roles(ROLE_ADMIN); :147 GET /audit/object/:t/:id; :214 GET /audit/:id]
  {
    key: 'audit.read',
    category: 'audit',
    op: 'read',
    label: 'View audit log',
    description: 'Read append-only audit log entries (admin-only).',
  },

  // ── company_master ────────────────────────────────────────────────────────
  // [Source: global company-master/register reads surfaced via firma; writes run as system worker]
  {
    key: 'company_master.read',
    category: 'company_master',
    op: 'read',
    label: 'Read company register',
    description: 'Access global cross-tenant company register data (Zefix/UID store).',
  },
  // PR#43-B1 — maintain THIS firm's own per-insurer placement fields (Ausschreibungs-E-Mail,
  // Antragsformular-Template) on the tenant `versicherer` overlay. This is NO LONGER a cross-tenant
  // register write: it is tenant-scoped, so a holder can only change its own firm's routing. Global
  // insurer data (logo, platform defaults) is platform-admin curated. Category stays `company_master`
  // to avoid a role-seed migration; the power it grants is strictly narrower than before.
  // [Source: carriers.controller.ts PATCH /carriers/:carrierUid/placement-settings]
  {
    key: 'carrier.manage',
    category: 'company_master',
    op: 'write',
    label: 'Manage own insurer placement settings',
    description: 'Maintain this firm\'s own Ausschreibungs-E-Mail and Antragsformular template per insurer (tenant-scoped). Global insurer data (logo, platform defaults) is curated by Brokly.',
  },

  // ── policy ────────────────────────────────────────────────────────────────
  // [Source: policy.controller.ts:79 GET /policies; :94,107,124 versions/version-at/:id; adjustments/endorsements/wage-basis GETs]
  {
    key: 'policy.read',
    category: 'policy',
    op: 'read',
    label: 'View policies',
    description: 'Read policy records, versions, adjustments, endorsements, and wage basis.',
  },
  // [Source: policy.controller.ts:66 POST /policies; :137 POST /policies/:id/versions; :151 DELETE; adjustment-endorsement.controller.ts:57,76,107; wage-basis.controller.ts:52; policy-upload.controller.ts:73]
  {
    key: 'policy.write',
    category: 'policy',
    op: 'write',
    label: 'Edit policies',
    description: 'Create, update, activate, and delete policies and their endorsements.',
  },
  // [Source: policy-migration.controller.ts:46,63 POST /policies/migrations @Roles('admin') — WinPower bulk import]
  {
    key: 'policy.migrate',
    category: 'policy',
    op: 'execute',
    label: 'Run policy migration',
    description: 'Trigger WinPower bulk import pipeline (admin-only).',
  },
  // P2 Placement Slice 2 — the Police-Ebene «Risiko & Deckung» tab (branchenspezifische
  // Ist-Attribute + Soll-Deckung & Gap). read/write are in BASIC_BASELINE_KEYS (every broker
  // captures a policy's risk/coverage, mirroring customer_risk.read/write on the Kunde level).
  // The besonders schuetzenswerte 91000-Gesundheitspruefung mirror keeps the existing customer
  // field-right + consent gate on top — this key alone never reveals it.
  // [Source: policy-risk.controller.ts GET/PATCH /policies/:id/risk-attributes + target-coverage]
  {
    key: 'policy_risk.read',
    category: 'policy',
    op: 'read',
    label: 'View policy risk & coverage',
    description: 'Read a policy\'s branchenspezifische Ist-Attribute and Soll-Deckung (Police-Ebene).',
  },
  {
    key: 'policy_risk.write',
    category: 'policy',
    op: 'write',
    label: 'Edit policy risk & coverage',
    description: 'Create and update a policy\'s Ist-Attribute and Soll-Deckung assessment (Police-Ebene).',
  },
  // P2 Placement Slice 3 — the per-tenant Regelwerk (branch_attribute_schema + target_coverage_catalog).
  // NOT in BASIC_BASELINE_KEYS: tailoring the house Regelwerk is an admin surface (Ch 9 names the
  // tenant admin role), not a per-broker baseline. read gates the admin GET; manage gates the
  // create/edit/deactivate/reset writes (the api is the hard line). [Source: regelwerk.controller.ts]
  {
    key: 'regelwerk.read',
    category: 'policy',
    op: 'read',
    label: 'View Regelwerk',
    description: 'Read the tenant Regelwerk: the field schema and the Soll-Deckung catalog.',
  },
  {
    key: 'regelwerk.manage',
    category: 'policy',
    op: 'write',
    label: 'Manage Regelwerk',
    description: 'Create, edit, deactivate and reset Soll-Deckung rules and schema fields.',
  },

  // ── invoice_check ─────────────────────────────────────────────────────────
  // [Source: invoice-check-orchestrator.controller.ts:43,53,63 @Roles('admin'); exception-handling.controller.ts:41,51,66]
  {
    key: 'invoice_check.operate',
    category: 'invoice_check',
    op: 'execute',
    label: 'Operate invoice check',
    description: 'Orchestrate, handle exceptions, scan overdue, and override invoice checks (admin-only).',
  },
  // [Source: commission-finance-gate.ts isFinanceAuthorized() — carrier-quality/deviation CHF amount fields]
  {
    key: 'invoice_check.read_amounts',
    category: 'invoice_check',
    op: 'read',
    label: 'View invoice amounts',
    description: 'See Finance amount fields on invoice-check results (carrier quality, deviation CHF).',
  },
  // [Source: tolerance-config.controller.ts:58 GET; :78 PUT :sparte; :101 POST testmodus — @Roles('admin')]
  {
    key: 'tolerance_config.manage',
    category: 'invoice_check',
    op: 'write',
    label: 'Manage tolerance config',
    description: 'View and update invoice tolerance configuration per Sparte (admin-only).',
  },

  // ── commission ────────────────────────────────────────────────────────────
  // [Source: non-amount commission reads — statement/line metadata, counts, %]
  {
    key: 'commission.read',
    category: 'commission',
    op: 'read',
    label: 'View commission statements',
    description: 'Read commission statement metadata, line counts, and percentages (no CHF amounts).',
  },
  // [Source: commission-finance-gate.ts isFinanceAuthorized() — rate/fixed/tiers, Kennzahlen CHF, split CHF, Akonto/expectation amounts]
  {
    key: 'commission.read_amounts',
    category: 'commission',
    op: 'read',
    label: 'View commission amounts',
    description: 'See Finance amount fields on commission lines (rates, CHF totals, Akonto, splits).',
  },
  // [Source: commission-exception.controller.ts:62 POST .../override @Roles('admin'); :79 transition; :97 carrier-quality; :109 akonto; commission-reconciliation.controller.ts:55; agreement-upload.controller.ts:61]
  {
    key: 'commission.operate',
    category: 'commission',
    op: 'execute',
    label: 'Operate commission processing',
    description: 'Override/transition exceptions, reconcile, run akonto, upload agreements (admin-only).',
  },
  // [Source: tariff-matrix.controller.ts:91,121,140,157 @Roles('admin'); code-table.controller.ts:69,87; broker-identity.controller.ts:74]
  {
    key: 'tariff.manage',
    category: 'commission',
    op: 'write',
    label: 'Manage tariff & code tables',
    description: 'CRUD tariff matrix, approve/deactivate tariffs, manage code tables and broker identity (admin-only).',
  },

  // ── renewals ──────────────────────────────────────────────────────────────
  // [Source: renewal-settings.controller.ts GET/PUT /admin/renewal-settings — admin-only config]
  {
    key: 'renewal_settings.manage',
    category: 'renewals',
    op: 'write',
    label: 'Manage renewal settings',
    description: 'View and update renewal alert thresholds and default Kündigungsfrist (admin-only).',
  },

  // ── customer_portal ───────────────────────────────────────────────────────
  // [Source: portal-config.controller.ts GET/PUT /admin/customer-portal — admin-only config]
  {
    key: 'portal_config.manage',
    category: 'customer_portal',
    op: 'write',
    label: 'Manage customer portal',
    description: 'View and update portal branding, feature toggles, invite criteria, and reminders (admin-only).',
  },
  // [Source: portal-invite.controller.ts POST /admin/customer-portal/invite/:subjectType/:subjectId — manual re-invite]
  // Story C2: manual (re-)invite of a single customer to the portal (admin-gated).
  // The scheduled auto-invite job does NOT use this permission (system-driven, no HTTP context).
  {
    key: 'portal_access.invite',
    category: 'customer_portal',
    op: 'write',
    label: 'Invite portal customers',
    description: 'Manually (re-)invite a customer to the portal (admin-gated).',
  },
  // [Source: portal-grant-admin.controller.ts GET /admin/customer-portal/grants/:subjectType/:subjectId]
  // Story V10-D: broker-side view of a customer's portal grants (Treuhänder / weitere Personen).
  // Admin-gated (NOT in BASIC_BASELINE_KEYS) — mirrors portal_access.invite.
  {
    key: 'portal_grant.read',
    category: 'customer_portal',
    op: 'read',
    label: 'View portal grants',
    description: 'List the additional persons a customer has invited to their portal (Treuhänder / weitere).',
  },
  // [Source: portal-grant-admin.controller.ts POST /admin/customer-portal/grants/:id/revoke]
  // Story V10-D: broker-side revoke of a portal grant (kills all live sessions of that grant).
  // op 'approve' — consequential state change (grant revoke), mirrors merge/override semantics.
  {
    key: 'portal_grant.revoke',
    category: 'customer_portal',
    op: 'approve',
    label: 'Revoke portal grants',
    description: 'Revoke an invited person\'s portal access on behalf of the customer (admin-gated).',
  },

  // ── agent ─────────────────────────────────────────────────────────────────
  // [Source: agent.controller.ts POST /agent/chat + GET /agent/conversation — Brok assistant]
  // Story P1a: every ordinary tenant user may open and chat with Brok.
  // Admin gets this via wildcard (no catalog entry needed for admin — the wildcard covers it).
  // Per-tool permission checks reuse EXISTING catalog keys (crm.read, policy.read, etc.) —
  // no additional keys are added here; the tool-runtime re-checks those at dispatch time.
  {
    key: 'agent.chat',
    category: 'agent',
    op: 'execute',
    label: 'Use Brok assistant',
    description: 'Open and chat with the Brok in-app assistant.',
  },

  // ── doc_generation ────────────────────────────────────────────────────────
  // [Source: doc-generation.controller.ts GET /doc-generation/templates + /templates/:id]
  // Story V10-B: automatic document creation (Kap. 18.1–18.5). read + generate are in the
  // basic baseline (every broker generates standard documents); template_manage is NOT
  // (central template administration — admin-seeded).
  {
    key: 'doc_generation.read',
    category: 'doc_generation',
    op: 'read',
    label: 'View document templates',
    description: 'List document templates and read their variable schemas (wizard support).',
  },
  // [Source: doc-generation.controller.ts POST /doc-generation/generate]
  {
    key: 'doc_generation.generate',
    category: 'doc_generation',
    op: 'execute',
    label: 'Generate documents',
    description: 'Render a document template with filled variables and dispatch it for signature.',
  },
  // [Source: doc-generation.controller.ts POST /doc-generation/templates]
  {
    key: 'doc_generation.template_manage',
    category: 'doc_generation',
    op: 'write',
    label: 'Manage document templates',
    description: 'Register, update and deactivate Word document templates.',
  },

  // ── channels ──────────────────────────────────────────────────────────────
  // [Source: channel-config.controller.ts GET /admin/channel-config + /matrix + customer order GETs]
  // Story V10-A: central Kanalsteuerung (Kap. 21) — admin-gated, NOT in basic baseline.
  {
    key: 'channel_config.read',
    category: 'channels',
    op: 'read',
    label: 'View channel configuration',
    description: 'Read the Kanalkatalog, Zustellreihenfolge overrides, and Kanal-Zuordnungsmatrix (admin-only).',
  },
  // [Source: channel-config.controller.ts PUT channels/:channelKey; PUT/DELETE customer order; POST/PATCH/DELETE matrix]
  {
    key: 'channel_config.manage',
    category: 'channels',
    op: 'write',
    label: 'Manage channel configuration',
    description: 'Update channel activation/priority/fallback, customer delivery orders, and matrix entries (admin-only).',
  },

  // ── signing ───────────────────────────────────────────────────────────────
  // Story V10-C: Zeichnungsprozess (Kap. 18.6) — managed multi-signer signing
  // workflow. read + operate are in the basic baseline (every broker drives a
  // signing run — consistent with vorgang.read/vorgang.work); signing_config.manage
  // is NOT baseline (central signer configuration — admin-seeded, mirrors
  // mahnwesen_config.manage).
  // [Source: signing-config.controller.ts GET /signing-config; signing.controller.ts GET /signing/:id]
  {
    key: 'signing.read',
    category: 'signing',
    op: 'read',
    label: 'View signing processes',
    description: 'Read signing configurations and signing-process states with per-signer statuses.',
  },
  // [Source: signing.controller.ts POST /signing/start, /signing/:id/advance, /signing/:id/abort, /signing/return]
  {
    key: 'signing.operate',
    category: 'signing',
    op: 'execute',
    label: 'Operate signing processes',
    description: 'Start, advance, abort, and match digital returns of managed signing runs.',
  },
  // [Source: signing-config.controller.ts PUT /signing-config — admin-seeded config, not in basic]
  {
    key: 'signing_config.manage',
    category: 'signing',
    op: 'write',
    label: 'Manage signing configuration',
    description: 'Configure signers and signing order per template/doc-type (admin-seeded, not in basic).',
  },

  // ── funktionsgruppen ──────────────────────────────────────────────────────
  // Story V10-F: task-routing teams (Stefan's matrix model, 2026-07-01 call).
  // Admin surface — NOT in BASIC_BASELINE_KEYS (mirrors role.read/role.manage
  // exclusion). The group QUEUE itself (GET /tasks/queue + claim) needs NO
  // catalog key — the whole tasks surface is JWT-only and the matrix model
  // demands the queue not depend on rights (D-F-QUEUE-JWT-ONLY).
  // [Source: funktionsgruppen.controller.ts GET /admin/funktionsgruppen* + GET /admin/users/:userId/funktionsgruppen]
  {
    key: 'funktionsgruppe.read',
    category: 'funktionsgruppen',
    op: 'read',
    label: 'View Funktionsgruppen',
    description: 'List task-routing groups, their members, and per-process routing targets.',
  },
  // [Source: funktionsgruppen.controller.ts POST/PATCH/DELETE /admin/funktionsgruppen* + membership mutations]
  {
    key: 'funktionsgruppe.manage',
    category: 'funktionsgruppen',
    op: 'write',
    label: 'Manage Funktionsgruppen',
    description: 'Create, edit, and delete task-routing groups; manage memberships and group routing.',
  },

  // ── search ────────────────────────────────────────────────────────────────
  // Story V10-K: the standalone tenant-wide global search (hybrid keyword +
  // semantic). IN the basic baseline — every broker uses the search bar. The
  // endpoint is RLS-scoped + read-only; it surfaces ONLY entities the tenant
  // already owns (no cross-tenant reach). NOT the Brok agent (agent.chat).
  // [Source: search.controller.ts GET/POST /search]
  {
    key: 'search.read',
    category: 'search',
    op: 'read',
    label: 'Use global search',
    description: 'Run tenant-wide keyword + semantic search across customers, policies, documents, and communications.',
  },

  // ── placement (Ausschreibungen) ─────────────────────────────────────────────
  // P2 Placement Slice 4 — the Ausschreibungs-/Placement object (the Offert-/Antragsprozess).
  // read/write are in BASIC_BASELINE_KEYS (every broker opens and drives an Ausschreibung for
  // their own customers, mirroring customer_risk / policy_risk read/write). The customer-scope RLS
  // + the `kunden.ausschreibungen` tab-visibility gate stay the hard lines on top of this key.
  // [Source: placement.controller.ts GET/POST /placements + GET/POST /placements/:id(+/abbrechen)]
  {
    key: 'placement.read',
    category: 'placement',
    op: 'read',
    label: 'View placements',
    description: 'Read a customer\'s Ausschreibungen (Placements), their pipeline, and their detail.',
  },
  {
    key: 'placement.write',
    category: 'placement',
    op: 'write',
    label: 'Edit placements',
    description: 'Create Ausschreibungen (Placements) and drive their lifecycle (incl. Abbrechen).',
  },

] as const satisfies readonly PermissionDefinition[];

// ── Role kinds (Story V10-F — Rolle/Gruppe semantic alignment) ────────────────

/**
 * role.kind values — mirrors the chk_role_kind CHECK constraint (migration 0055).
 *
 * Stefan's prototype distinguishes ONE base "Rolle" per user from N additive
 * "Berechtigungsgruppen"; both are `role` rows — `kind` is a UI/display
 * discriminator ONLY. The permission resolver ignores it ("Gruppen sind additiv
 * zur Rolle" ≡ the Epic-12 union-of-roles). Immutable after create.
 * NOT to be confused with Funktionsgruppen (task routing — a separate entity).
 */
export const ROLE_KINDS = ['rolle', 'gruppe'] as const;

/** Union type of the two role kinds. */
export type RoleKind = (typeof ROLE_KINDS)[number];

// ── Derived types + helpers ────────────────────────────────────────────────────

/**
 * Union type of every valid permission key (narrow string literals).
 * Import this for type-safe references in guards, seeds, and the role API.
 */
export type PermissionKey = (typeof PERMISSION_CATALOG)[number]['key'];

/**
 * Flat readonly array of every permission key (in catalog order).
 * Used by the guard (union of all keys), seed (basic baseline check), and wizard (catalog endpoint).
 */
export const PERMISSION_KEYS = PERMISSION_CATALOG.map((p) => p.key) as readonly PermissionKey[];

/**
 * Return all permission definitions that belong to a given wizard category.
 * Pure function, no side effects. Used by Story 12.6's catalog endpoint and the wizard.
 *
 * @param category - a PERMISSION_CATEGORIES key
 * @returns filtered slice of PERMISSION_CATALOG in catalog order
 */
export function getPermissionsByCategory(category: PermissionCategoryKey): readonly PermissionDefinition[] {
  return PERMISSION_CATALOG.filter((p) => p.category === category);
}

// ── Basic baseline ─────────────────────────────────────────────────────────────

/**
 * The 23 permission keys granted to every new user by default (the `basic` role seed).
 *
 * These are exactly the OPEN (non-admin-gated) operations from the Traceability table
 * (Story 12.1 Dev Notes). Any key that is admin-gated today — merge, override,
 * tag-definition CRUD, all /admin/* routes, blacklist, Finance amounts, intake/webhook
 * (machine-facing), role admin, comm_access.grant_managed, mahnwesen_config.manage,
 * comm_access.grant_managed, policy.migrate, invoice_check.operate, invoice_check.read_amounts,
 * tolerance_config.manage, commission.*, tariff.manage, audit.read,
 * routing_policy.*, whitelist.*, tenant.*, role.*, document.delete (DMS M3,
 * admin-only hard delete) — is intentionally EXCLUDED.
 *
 * Admin keeps everything via the wildcard (no keys enumerated for `admin`);
 * basic = today's open-route behaviour for ordinary tenant users.
 *
 * Story P1a: `agent.chat` added — every ordinary user may use Brok.
 *
 * `satisfies readonly PermissionKey[]` is the compile-time guarantee that every
 * key listed here is a real entry in PERMISSION_CATALOG (AC4 ⊆ guarantee).
 *
 * [Source: docs/prd/epic-12-dynamic-rbac.md "Seeding (decision #2)" → "basic baseline key set"]
 * [Source: Story 12.1 Dev Notes — "Basic baseline set (the 22 keys)"]
 * [Source: Story P1a spec §2 — agent.chat added to basic baseline]
 */
export const BASIC_BASELINE_KEYS = [
  'crm.read',
  'crm.write',
  'relationship.read',
  'relationship.write',
  'firma.enrich',
  // P2 Placement Slice 1 — every broker captures + edits the Kunde Risikoprofil + Basissparten
  // (the gated Gesundheit/Einkommen fields keep their separate field-right + consent gate). No
  // top-up migration for already-seeded tenants (the P1a/V10-E/UI-U8 precedent: the seed grants
  // keys only on role creation; admins keep them via the wildcard).
  'customer_risk.read',
  'customer_risk.write',
  'person.merge_suggestions',
  'tag.read',
  'tag.assign',
  'customer_status.read',
  'customer_status.transition',
  'mandate.dispatch',
  'mandate.eingangskontrolle',
  'vorgang.read',
  'vorgang.work',
  'document.upload',
  'document.read_link',
  // DMS M3 (dms-permissions): Ablage read/download/state are open, basic-user
  // operations (mirrors document.upload/document.read_link precedent);
  // document.delete stays admin-gated — NOT added here.
  'document.read',
  'document.download',
  'document.set_state',
  // DMS docs-fix: manual reclassify (PATCH /documents/:id) — a basic-user
  // Ablage operation (mirrors document.set_state, a write on the same row);
  // document.delete stays admin-gated — NOT added here.
  'document.update',
  'communication.read',
  'comm_access.request',
  'comm_access.decide',
  // Story UI-U8: manual email referencing (Kap. 7). No top-up migration for
  // already-seeded tenants (the seed grants keys only on role creation — the
  // P1a/V10-E precedent); admins keep it via the wildcard.
  'comm_reference.manage',
  // Story MC5: outbound send from a connected mailbox. No top-up migration for
  // already-seeded tenants (the seed grants keys only on role creation — the
  // P1a/V10-E/UI-U8 precedent); admins keep it via the wildcard.
  'communication.send',
  'company_master.read',
  'policy.read',
  'policy.write',
  // P2 Placement Slice 2 — every broker captures + edits a policy's Risiko & Deckung (Police-Ebene),
  // mirroring customer_risk.read/write on the Kunde level. No top-up migration for already-seeded
  // tenants (the seed grants keys only on role creation; admins keep them via the wildcard).
  'policy_risk.read',
  'policy_risk.write',
  'agent.chat',
  // Story V10-B: every ordinary user may read templates + generate documents
  // (template_manage stays admin-gated — NOT in basic).
  'doc_generation.read',
  'doc_generation.generate',
  // Story V10-C: every ordinary user may view + operate signing processes
  // (consistent with vorgang.read/vorgang.work; signing_config.manage stays
  // admin-gated — NOT in basic).
  'signing.read',
  'signing.operate',
  // Story V10-E: every ordinary user may fetch their OWN effective denied
  // visibility set (UI shaping). P1a agent.chat precedent: NO top-up migration
  // for already-seeded tenants (the seed grants keys only on role creation —
  // ensureRole returns early for existing roles; admins can grant via the 12.6 API).
  // visibility.read / visibility.manage stay admin-gated — NOT in basic.
  'visibility.read_own',
  // Story V10-K: every ordinary user may use the global search bar (read-only,
  // RLS-scoped, own-tenant only).
  'search.read',
  // P2 Placement Slice 4 — every broker opens + drives an Ausschreibung for their own customers
  // (mirrors customer_risk / policy_risk read/write). No top-up migration for already-seeded
  // tenants (the seed grants keys only on role creation; admins keep them via the wildcard).
  'placement.read',
  'placement.write',
] as const satisfies readonly PermissionKey[];

// ── Administrationsrolle vs Kundenrolle separation (V3.0 §5.8, WS1) ───────────

/**
 * The permission CATEGORIES an Administrationsrolle (`role.ist_administrationsrolle=true`)
 * may hold. These are the pure PARAMETRIZATION surfaces — configuring the system,
 * roles, teams, task-routing groups, block-/exclusion-lists, and reading the audit
 * log — none of which is operative access to a customer's data.
 *
 * Spec §5.8: an admin role holds ONLY parametrization functions; customer access
 * always requires a SEPARATE Kundenrolle (+ team membership). Admin status alone
 * (even + team) NEVER suffices ("Brokeradmin" = the pure admin role, not a combo).
 * Validation REJECTS any admin role carrying a customer-data right.
 *
 * [Source: meetings/2026-08-24_ANALYSIS_berechtigungskonzept.md §2.4 (5.8) + §2.9 UC1/UC5]
 */
export const ADMIN_ROLE_ALLOWED_CATEGORIES: readonly PermissionCategoryKey[] = [
  'admin',            // whitelist/tenant settings + own-org team + Kontrolle read
  'access',           // role/permission + visibility config (the wizard itself)
  'audit',            // audit-log read
  'blacklist',        // block-list management
  'suppression',      // Ausschlussliste management
  'funktionsgruppen', // task-routing group management
];

/**
 * Individual PARAMETRIZATION keys that live INSIDE otherwise customer-data
 * categories (whose category also carries operative keys, so we allow them
 * key-by-key). Every one is a *_config / *_settings / template / routing /
 * tariff-and-code-table MANAGEMENT key — tenant configuration, not a customer's
 * record. [Source: §5.8 "manage groups/teams/task-groups/blocklists/settings"]
 */
export const ADMIN_ROLE_ALLOWED_KEYS: readonly PermissionKey[] = [
  'tag_definition.manage',
  'mahnwesen_config.manage',
  'routing_policy.read',
  'routing_policy.manage',
  'tolerance_config.manage',
  'renewal_settings.manage',
  'portal_config.manage',
  'channel_config.read',
  'channel_config.manage',
  'signing_config.manage',
  'doc_generation.template_manage',
  'tariff.manage',
];

/**
 * Is `key` a pure PARAMETRIZATION permission (allowed for an Administrationsrolle)?
 * An unknown key (not in the catalog) returns false — treated as customer-data, so
 * the §5.8 validation fails closed. Callers validate keys ∈ catalog first (400) so
 * an unknown key never reaches this check in practice.
 */
export function isParametrizationPermission(key: string): boolean {
  const def = PERMISSION_CATALOG.find((p) => p.key === key);
  if (!def) return false;
  return (
    (ADMIN_ROLE_ALLOWED_CATEGORIES as readonly string[]).includes(def.category) ||
    (ADMIN_ROLE_ALLOWED_KEYS as readonly string[]).includes(key)
  );
}

/**
 * Given a permission-key set, return the subset that is CUSTOMER-DATA / operative
 * (i.e. NOT parametrization) — exactly the keys an Administrationsrolle may NOT
 * hold (§5.8). Empty array = the set is admin-clean.
 */
export function getCustomerDataPermissionKeys(keys: readonly string[]): string[] {
  return keys.filter((k) => !isParametrizationPermission(k));
}

// ── Read-only role (Part A — mandatory per-tenant READ-ONLY role) ─────────────

/**
 * The canonical name of the mandatory per-tenant READ-ONLY system role.
 *
 * Seeded (is_system=true, is_wildcard=false) for EVERY tenant by RoleSeedService
 * (boot backfill + tenant-creation), and auto-assigned to first-login SSO users
 * (SsoJitProvisioningService — Part B). It is a normal `role` row of kind 'rolle',
 * so it appears + is assignable in Administration › Rollen/Rechte + Mitglieder like
 * any other role. Admins re-assign a HIGHER role to a read-only user via the same
 * assign surface (which resolves any pending access request — Part E).
 */
export const READ_ONLY_ROLE_NAME = 'read-only' as const;

/**
 * The permission keys granted to the `read-only` role: the READ/VIEW subset of the
 * basic baseline — every baseline key whose catalog `op` is `'read'`. No
 * write / manage / create / delete / execute / approve keys, so a read-only user
 * can VIEW everything an ordinary (basic) user can view but change nothing.
 *
 * Derived from BASIC_BASELINE_KEYS (not the whole catalog) so it stays in sync when
 * the baseline changes AND so admin-only read surfaces (audit.read, whitelist.read,
 * tenant.read, routing_policy.read, commission.read, channel_config.read, …) are
 * intentionally EXCLUDED — a read-only user is a demoted ordinary user, never an
 * admin-viewer. `satisfies readonly PermissionKey[]` guarantees every derived key is
 * a real catalog entry.
 */
export const READ_ONLY_BASELINE_KEYS: readonly PermissionKey[] = BASIC_BASELINE_KEYS.filter(
  (key) => PERMISSION_CATALOG.find((p) => p.key === key)?.op === 'read',
);

// ── Effective-permissions read (Story UI-U6, Task 1) ──────────────────────────

/**
 * Response body for the NEW `GET /me/effective-permissions` read (Story UI-U6).
 *
 * Mirrors the V10-E `EffectiveVisibilityResponse` pattern: the guard write-back
 * resolves the caller's permission Set + wildcard flag, the controller serialises
 * them (no extra DB round trip). The web renders nav/pages/actions from this
 * response and NEVER recomputes auth rules — the api guards remain the hard line.
 *
 * WILDCARD CONTRACT: `permission_keys` is EMPTY when `is_wildcard` is true (the
 * `admin` wildcard short-circuits before the set is read — mirrors AuthContext).
 * The web's `can(key)` therefore MUST check `is_wildcard || keys.includes(key)`
 * (mirrors the api's own `hasPermission`, rendered from server data).
 *
 * [Source: packages/shared/src/visibility.ts EffectiveVisibilityResponse]
 * [Source: apps/api/src/auth/auth-context.ts#hasPermission]
 */
export interface EffectivePermissionsResponse {
  /** The caller's effective permission keys, sorted ascending. Empty when is_wildcard. */
  permission_keys: string[];
  /** true iff the caller holds the `admin` wildcard role (grants every key). */
  is_wildcard: boolean;
}

// ── Admin members read (Story UI-U6, Task 1) ──────────────────────────────────

/**
 * A single tenant member row for the NEW `GET /admin/members` read (Story UI-U6).
 *
 * Wire = snake_case; `tenant_id` is NEVER on the wire (implicit from the token).
 * `role_names` aggregates the user's Epic-12 role names (empty array when none).
 * `membership_role` is the coarse `membership.role` enum (distinct from Epic-12
 * dynamic roles). `display_name` is nullable (app_user may have none).
 *
 * [Source: apps/api/src/modules/access/members-read.service.ts]
 */
export interface AdminMemberDto {
  user_id: string;
  display_name: string | null;
  email: string;
  membership_role: MembershipRole;
  role_names: string[];
  created_at: string;
}

/**
 * Combined lifecycle state for a row in the Administration › Mitglieder table.
 * `active` = a real member (has a membership); `invited` = a broker_invitation
 * whose token was minted + mailed (awaiting activation); `pending` = a
 * broker_invitation that is queued but not yet sent (no token minted).
 */
export type AdminMemberState = 'active' | 'invited' | 'pending';

/**
 * A single un-consumed broker invitation for the `GET /admin/members` read
 * (Fix 5 — the main-app invite surface). Wire = snake_case; `tenant_id` is NEVER
 * on the wire (implicit from the token). `role_id` is the copied tenant role the
 * activation flow grants (null = default `basic` at activation).
 *
 * [Source: apps/api/src/modules/access/members-read.service.ts]
 */
export interface AdminInvitationDto {
  id: string;
  email: string;
  display_name: string;
  role_id: string | null;
  /** `invited` = mail sent (token minted); `pending` = queued, not yet sent. */
  state: Exclude<AdminMemberState, 'active'>;
  created_at: string;
}

/**
 * Response body for `GET /admin/members` (Story UI-U6; extended by Fix 5).
 * `invitations` carries the tenant's un-consumed broker invitations so the
 * Mitglieder table can render pending members with the 3-state badge.
 */
export interface AdminMembersResponse {
  members: AdminMemberDto[];
  invitations: AdminInvitationDto[];
}
